import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { JanelaSolicitacaoHonraService } from '../janela-solicitacao-honra.service';
import { ResultadoPaginado } from 'src/app/model/resultado';
import { JanelaSolicitacaoHonra, JanelaSolicitacaoHonraEnvio, JanelaSolicitacaoHonraResposta } from 'src/app/model/janela-solicitacao-honra.model';
import { EntityPagedRequestParameters, EntityPagedResult } from '@bndes/bndes-ux4/bndes-angular-utils';
import { BnModalMessageService } from '@bndes/bndes-ux4/bn-modal-message';
import { RetornoMensagemSimplesDTO } from 'src/app/model/questionario.model';
import { MensagemService } from 'src/app/features/mensagem.service';
import moment from 'moment';
import { PermissaoService } from 'src/app/shared/permissao.service';
import { ROLE_GESTOR_CONSULTA, ROLE_GESTOR_ENVIO } from 'src/app/constantes/permissao.constants';
import { MENSAGENS_ERRO } from 'src/app/shared/fgi-mensagens-erro.model.';
import { Router } from '@angular/router';
import { DataSistemaService } from 'src/app/shared/services/data-sistema.service';

@Component({
  selector: 'app-janela-solicitacao-honra-pesquisa',
  templateUrl: './janela-solicitacao-honra-pesquisa.component.html',
  styleUrl: './janela-solicitacao-honra-pesquisa.component.scss',
  standalone: false,
})
export class JanelaSolicitacaoHonraPesquisaComponent implements OnInit {
  private readonly janelaService = inject(JanelaSolicitacaoHonraService);
  private readonly modalService = inject(BnModalMessageService);
  private readonly mensagemService = inject(MensagemService);
  private readonly permissaoService = inject(PermissaoService);
  private readonly dataService = inject(DataSistemaService);
  private readonly router = inject(Router);

  public readonly opcoesPaginacao = [10, 15, 20];

  public permissaoEnvio: boolean = false;
  
  public idJanela: number = null;
  public dataJanela: Date = null;
  public pesquisado = false;
  public mensagemCampoInvalido: string = null;
  
  private dataSistema: Date = new Date();
  public dataExtensao: Date = null;
  public dataMaxima: Date;
  public dataMinima: Date;
  
  public janelasPaginadas: JanelaSolicitacaoHonra[] = [];
  public atributosPaginacao: EntityPagedResult<JanelaSolicitacaoHonra>;

  @ViewChild('modal') modalTemplate: TemplateRef<any>;
  @ViewChild('botoes') modalBotoes: TemplateRef<any>;

  ngOnInit(): void {
    this.carregarDataSistema();
    this.permissaoService
      .getPermissoesUsuarioAutenticado()
      .subscribe((resp) => {
        this.permissaoEnvio = resp && resp[ROLE_GESTOR_ENVIO];

        this.trataPermissaoConsulta(resp && resp[ROLE_GESTOR_CONSULTA]);
      });

    this.atributosPaginacao = {
      page: 1,
      pageSize: this.opcoesPaginacao[0],
      pages: undefined,
      count: undefined,
      list: undefined
    };
  }

  buscar(primeiraPagina: boolean): void {
    this.pesquisado = false;

    if (primeiraPagina)
      this.atributosPaginacao.page = 1;

    const params: JanelaSolicitacaoHonraEnvio = {
      pagina: this.atributosPaginacao.page,
      limite: this.atributosPaginacao.pageSize
    }

    if (this.idJanela !== null)
      params.idJanela = this.idJanela;

    if (this.dataJanela !== null)
      params.nomeJanela = this.transformarData(this.dataJanela, 'MM/YYYY');

    this.janelaService.getJanelas(params).subscribe({
      next: (janelas: ResultadoPaginado<JanelaSolicitacaoHonraResposta>) => {
        this.janelasPaginadas = this.converteJanelasDto(janelas.resultados);
        this.atributosPaginacao.count = janelas?.totalRegistrosSemPaginacao || 0;
        this.pesquisado = true;
      },
      error: err => {
        console.error(err);
      }
    });
  }

  abrirModal(janela: JanelaSolicitacaoHonra): void {
    if (!this.permissaoEnvio) {
      this.mensagemService.adicionarMensagemErro(MENSAGENS_ERRO[0].semPermissaoPadrao);
      return;
    }

    this.setDatasPopup(janela);
    this.modalService.abrir('Estender Período', null, 'md', this.modalTemplate, this.modalBotoes)
    .result.then((result) => { 
      if (result === 'ok') {
        this.estender(janela);
      }
    })
    .catch(() => {
      this.dataExtensao = null;
    });
  }

  estender(janela: JanelaSolicitacaoHonra): void {
    this.janelaService
    .estenderPeriodo(janela.id, this.transformarData(this.dataExtensao, 'YYYYMMDD'))
    .subscribe({
      next: (retorno: RetornoMensagemSimplesDTO) => {
        this.buscar(false);
        this.mensagemService.adicionarMensagemSucesso(
          retorno.mensagem
        );
      },
      error: err => { 
        console.error(err);
      }
    });
  }

  trataPermissaoConsulta(permissao: boolean): void {
    if (!permissao) {
      this.mensagemService.adicionarMensagemErro(MENSAGENS_ERRO[0].semPermissaoPadrao);
      this.router.navigate(['/home']);
      return;
    }
  }

  get dataValida(): boolean {
    if (!(this.dataExtensao instanceof Date)) {
      this.mensagemCampoInvalido = `Data inválida`;

      return false;
    }

    if (this.dataExtensao > this.dataMaxima) {
      this.mensagemCampoInvalido = 
        `A nova data não pode ser posterior ao dia ${this.transformarData(this.dataMaxima, 'DD/MM/YYYY')}`;

      return false;
    }
    
    if (this.dataExtensao < this.dataMinima) {
      this.mensagemCampoInvalido = 
        `A nova data não pode ser anterior à data de fechamento atual`;
        
      return false;
    }
    
    return true;
  }

  mudarPaginacao(novosAtributosPaginacao: EntityPagedRequestParameters): void {
    this.atributosPaginacao.page = novosAtributosPaginacao.page;
    this.atributosPaginacao.pageSize = novosAtributosPaginacao.pageSize;
    this.buscar(false);
  }
  
  converteJanelasDto(janelasDto: JanelaSolicitacaoHonraResposta[]): JanelaSolicitacaoHonra[] {
    const janelas = janelasDto.map(janelaDto => {
      const janela = new JanelaSolicitacaoHonra();
    
      Object.assign(janela, janelaDto, {
        dataAbertura: this.converterData(janelaDto.dataAbertura),
        dataFechamento: this.converterData(janelaDto.dataFechamento),
        dataInicioProcessamento: this.converterData(janelaDto.dataInicioProcessamento),
      });

      return janela;
    });
    return janelas;
  }

  confirmarExtensao(): void {
    this.modalService.close('ok');
  }
  
  cancelarExtensao(): void {
    this.modalService.dismiss();
  }

  setDatasPopup(janela: JanelaSolicitacaoHonra): void {
    const dataFechamentoAtual = janela.dataFechamento 
      ? janela.dataFechamento 
      : janela.dataAbertura;

    if (dataFechamentoAtual.getDate() < 15) dataFechamentoAtual.setDate(15);
    this.dataMaxima = new Date(dataFechamentoAtual);
    this.dataMaxima.setDate(20);
    
    this.dataMinima = new Date(dataFechamentoAtual);
    this.dataMinima.setDate(dataFechamentoAtual.getDate() + 1);
  }

  carregarDataSistema(): void {
    this.dataService.getDataCorrente().subscribe({
      next: (data: Date) => {
        this.dataSistema = data;
        console.log(this.dataSistema);
      },
      error: err => {
        this.dataSistema = new Date();
        console.error(err);
      }
    });
  }

  transformarData(dataJanela: Date, formato: string): string {
    return moment(dataJanela).format(formato);
  }

  converterData(data: string): Date | null {
    if (!data) return null;

    const [ano, mes, dia] = data.split('-').map(Number);

    return new Date(ano, mes - 1, dia);
  }

  limpar(): void {
    this.idJanela = null;
    this.dataJanela = null;
    this.pesquisado = false;

    this.atributosPaginacao.page = 1;
  }
}